import React, { FunctionComponent, useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import {
  CostMessagingContentType,
  CostMessagingModalContent,
  CostMessagingModalCoverageInfo,
  CoverageType,
  EAP_MEDS_CONTENT_TYPE,
  EAP_NEURO_CONTENT_TYPE,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { CustomerName } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customer'
import { coverageBenefitsMessages } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import {
  getSessionCountForDisplay,
  isUnlimitedSessionLimit,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'

import { ActivityBadge } from '../../atoms/activityBadge/ActivityBadge'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { IllustrationBadge } from '../../atoms/illustrationBadge/IllustrationBadge'
import { AuroraClinicalLeaveEvaluationSpotIllustration } from '../../atoms/illustrations/AuroraClinicalLeaveEvaluationSpotIllustration'
import { AuroraCoachingSpotIllustration } from '../../atoms/illustrations/AuroraCoachingSpotIllustration'
import { AuroraControlledMedsSpotIllustration } from '../../atoms/illustrations/AuroraControlledMedsSpotIllustration'
import { AuroraIndividualTherapySpotIllustration } from '../../atoms/illustrations/AuroraIndividualTherapySpotIllustration'
import { AuroraNeurodiversitySpotIllustration } from '../../atoms/illustrations/AuroraNeurodiversitySpotIllustration'
import { AuroraParentCoachingSpotIllustration } from '../../atoms/illustrations/AuroraParentCoachingSpotIllustration'
import { ChildTherapySecondaryRec } from '../../atoms/illustrations/ChildTherapySecondaryRec'
import { CoachingSecondaryRec } from '../../atoms/illustrations/CoachingSecondaryRec'
import { ControlledMedsSecondaryRec } from '../../atoms/illustrations/ControlledMedsSecondaryRec'
import { NeurodiversityCOEIllustration } from '../../atoms/illustrations/NeurodiversityCOEIllustration'
import { ProfileBookingShortTermDisabilityEvaluationIllustration } from '../../atoms/illustrations/ProfileBookingShortTermDisabilityEvaluationIllustration'
import { TherapySecondaryRec } from '../../atoms/illustrations/TherapySecondaryRec'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { Title } from '../../atoms/title/Title'
import { BadgeShape, BadgeVariant } from '../../badge/constants'
import { AppContext } from '../../context'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface CoverageBenefitsSummaryActionButton {
  text: React.ReactNode
  onPress: () => void
  testID?: string
}

export interface CoverageBenefitsSummaryProps {
  coverageModalTitleText?: React.ReactNode
  coverageModalContents: CostMessagingModalContent[]
  costMessagingModalCoverageInfo?: CostMessagingModalCoverageInfo
  customerName?: string
  numSessions?: string
  numberOfSessionByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  subheaderText?: string
  primaryButton?: CoverageBenefitsSummaryActionButton
  secondaryButton?: CoverageBenefitsSummaryActionButton
  /**
   * Optional content rendered inside the top content group, above the title
   * (e.g. a close icon when used inside a modal).
   */
  topSlot?: React.ReactNode
  /**
   * Horizontal alignment of the action button container.
   * Defaults to 'flex-end' (the modal pattern). Use 'center' when the summary
   * lives inline on a page (e.g. Smart Search results).
   */
  buttonAlignment?: 'flex-end' | 'center' | 'flex-start' | 'stretch'
  /**
   * Prefix used for internal testIDs (badges, contentContainer, etc.).
   * Defaults to 'CoverageBenefitsSummary'. CoverageMessagingModal passes
   * 'CoverageMessagingModal' to preserve historical Cypress selectors.
   */
  testIDPrefix?: string
}

const CoverageContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: 'auto',
  paddingBottom: theme.spacing['16px'],
}))

const CoverageContentRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  paddingVertical: theme.spacing['16px'],
}))

const CoverageContentText = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '80%',
  flexDirection: 'column',
  marginLeft: theme.spacing['12px'],
}))

const CoverageContentHeader = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  ...(!theme.breakpoints.isMobileSized && { alignItems: 'baseline' }),
  flexWrap: 'wrap',
  gap: theme.spacing['8px'],
  paddingBottom: theme.spacing['4px'],
}))

const TitleText = styled(Title)<{ theme: ThemeType; hasTitle: boolean }>(({ theme, hasTitle }) => ({
  marginBottom: hasTitle
    ? theme.spacing['8px']
    : theme.breakpoints.isMobileSized
    ? theme.spacing['12px']
    : theme.spacing['16px'],
}))

const SubheaderBodyText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['12px'] : theme.spacing['16px'],
}))

const ButtonContainer = styled.View<{ theme: ThemeType; alignment: 'flex-end' | 'center' | 'flex-start' | 'stretch' }>(
  ({ theme, alignment }) => ({
    alignSelf: alignment,
    width: theme.breakpoints.isMobileSized ? '100%' : 'auto',
    marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : 0,
    flexDirection: theme.breakpoints.isMobileSized ? 'column-reverse' : 'row',
    gap: theme.spacing['16px'],
  }),
)

const hasHPIDescription = (costMessagingModalCoverageInfo?: CostMessagingModalCoverageInfo) => {
  return (
    costMessagingModalCoverageInfo && costMessagingModalCoverageInfo?.customerCoverageType === CoverageType.HPI_ONLY
  )
}

const programsWithNoCostBadge: CostMessagingContentType[] = [
  ProgramNames.BlendedCareTherapy,
  ProgramNames.Coaching,
  ProgramNames.CoachingForParents,
  'TherapyForChildren',
  EAP_MEDS_CONTENT_TYPE,
  EAP_NEURO_CONTENT_TYPE,
]

const programsWithCostSharingBadge: CostMessagingContentType[] = [
  ProgramNames.MedicationManagement,
  'TherapyOrCoaching',
]

const getShouldDisplayNoCostBadge = (contentType: CostMessagingContentType, hpiOnly: boolean) => {
  return !hpiOnly && programsWithNoCostBadge.includes(contentType)
}

const getShouldDisplayCostSharingBadge = (
  contentType: CostMessagingContentType,
  hpiOnly: boolean,
  isAmazon: boolean,
  isAmazonCostTransparencyEnabled: boolean,
) => {
  return !hpiOnly && isAmazon && isAmazonCostTransparencyEnabled && programsWithCostSharingBadge.includes(contentType)
}

export const CoverageBenefitsSummary: FunctionComponent<CoverageBenefitsSummaryProps> = ({
  coverageModalTitleText,
  coverageModalContents,
  costMessagingModalCoverageInfo,
  customerName,
  numSessions,
  numberOfSessionByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
  subheaderText,
  primaryButton,
  secondaryButton,
  topSlot,
  buttonAlignment = 'flex-end',
  testIDPrefix = 'CoverageBenefitsSummary',
}) => {
  const { formatMessage } = useIntl()
  const {
    colors,
    breakpoints: { isMobileSized },
    isAuroraTheme,
  } = useTheme() as ThemeType
  const { isCustomerDynamicSessionLimitEnabled, isAmazonCostTransparencyEnabled } = useContext(AppContext)

  const isAmazon = customerName?.toLowerCase() === CustomerName.AMAZON
  const hasCustomerName = !!customerName && customerName.length > 0

  const { therapySessions, coachingSessions, neurodiversitySessions } = getSessionCountForDisplay({
    numSessions,
    numberOfSessionsByProgram: numberOfSessionByProgram,
    displayVisitsPerIssuePerYear,
    displayVisitsPerIssuePerYearByProgram,
  })

  const hpiOnly = !!hasHPIDescription(costMessagingModalCoverageInfo)

  const renderActivityBadges = useCallback(
    (contentType: CostMessagingContentType): React.ReactNode => {
      const shouldDisplayNoCostBadge = getShouldDisplayNoCostBadge(contentType, hpiOnly)
      const shouldDisplayCostSharingBadge = getShouldDisplayCostSharingBadge(
        contentType,
        hpiOnly,
        isAmazon,
        isAmazonCostTransparencyEnabled ?? false,
      )

      return (
        <>
          {shouldDisplayNoCostBadge && (
            <ActivityBadge
              text={
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'No cost',
                    description: 'Cost messaging modal badge to indicate therapy or coaching sessions are covered',
                  })}
                  size={BodyTextSize.BADGE}
                  color={colors.components.badge.infoPrimary.text}
                />
              }
              variant={BadgeVariant.INFO_PRIMARY}
              shape={BadgeShape.ROUND}
              preserveCase={true}
              testID={tID(`${testIDPrefix}-no-cost-badge-${contentType}`)}
            />
          )}
          {shouldDisplayCostSharingBadge && (
            <ActivityBadge
              text={
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'Cost sharing',
                    description: 'Cost messaging modal badge to indicate a given program is eligible for cost sharing',
                  })}
                  size={BodyTextSize.BADGE}
                  color={colors.textSuccessAlternate}
                />
              }
              variant={BadgeVariant.SUCCESS}
              shape={BadgeShape.ROUND}
              preserveCase={true}
              testID={tID(`${testIDPrefix}-cost-sharing-badge-${contentType}`)}
            />
          )}
        </>
      )
    },
    [formatMessage, colors, hpiOnly, isAmazon, isAmazonCostTransparencyEnabled, testIDPrefix],
  )

  const renderProgramIllustration = (contentType: CostMessagingContentType): React.ReactElement | null => {
    if (isAuroraTheme) {
      switch (contentType) {
        case ProgramNames.CoachingForParents:
        case ProgramNames.Coaching:
          return <AuroraCoachingSpotIllustration size={60} />
        case 'TherapyForChildren':
          return <AuroraParentCoachingSpotIllustration size={60} />
        case 'TherapyOrCoaching':
        case 'ThatchTherapy':
        case ProgramNames.BlendedCareTherapy:
          return <AuroraIndividualTherapySpotIllustration size={60} />
        case ProgramNames.MedicationManagement:
        case EAP_MEDS_CONTENT_TYPE:
          return <AuroraControlledMedsSpotIllustration size={60} />
        case ProgramNames.ClinicalLeaveEvaluation:
          return <AuroraClinicalLeaveEvaluationSpotIllustration size={60} />
        case EAP_NEURO_CONTENT_TYPE:
          return <AuroraNeurodiversitySpotIllustration size={60} />
        default:
          return null
      }
    }
    switch (contentType) {
      case ProgramNames.CoachingForParents:
      case ProgramNames.Coaching:
        return <CoachingSecondaryRec width={60} height={60} />
      case 'TherapyForChildren':
        return <ChildTherapySecondaryRec width={60} height={60} />
      case 'TherapyOrCoaching':
      case 'ThatchTherapy':
      case ProgramNames.BlendedCareTherapy:
        return <TherapySecondaryRec width={60} height={60} />
      case ProgramNames.MedicationManagement:
      case EAP_MEDS_CONTENT_TYPE:
        return <ControlledMedsSecondaryRec width={60} height={60} />
      case ProgramNames.ClinicalLeaveEvaluation:
        return <ProfileBookingShortTermDisabilityEvaluationIllustration size={60} />
      case EAP_NEURO_CONTENT_TYPE:
        return <NeurodiversityCOEIllustration size={60} />
      default:
        return null
    }
  }

  const getCoverageIcon = (contentType: CostMessagingContentType) => {
    const baseIcon = renderProgramIllustration(contentType)
    if (!baseIcon) return null

    const wrapWithSessionBadge = (sessionText: string | number) => (
      <IllustrationBadge
        text={<BodyText size={BodyTextSize.SMALL} text={String(sessionText)} />}
        topOffset={40}
        leftOffset={40}
        icon={baseIcon}
      />
    )

    switch (contentType) {
      case ProgramNames.CoachingForParents:
      case ProgramNames.Coaching:
        return coachingSessions &&
          coachingSessions !== 0 &&
          !isUnlimitedSessionLimit(Number(coachingSessions)) &&
          !isCustomerDynamicSessionLimitEnabled
          ? wrapWithSessionBadge(coachingSessions)
          : baseIcon
      case 'TherapyForChildren':
      case 'ThatchTherapy':
      case ProgramNames.BlendedCareTherapy:
        return therapySessions && therapySessions !== '0' && !isCustomerDynamicSessionLimitEnabled
          ? wrapWithSessionBadge(therapySessions)
          : baseIcon
      case EAP_NEURO_CONTENT_TYPE:
        return neurodiversitySessions &&
          neurodiversitySessions !== 0 &&
          !isUnlimitedSessionLimit(Number(neurodiversitySessions)) &&
          !isCustomerDynamicSessionLimitEnabled
          ? wrapWithSessionBadge(neurodiversitySessions)
          : baseIcon
      default:
        return baseIcon
    }
  }

  return (
    <>
      <View testID={tID(testIDPrefix)}>
        {topSlot}
        <TitleText
          text={
            coverageModalTitleText ?? (
              <FormattedMessage
                {...coverageBenefitsMessages.ANNUAL_BENEFITS_TITLE}
                values={{ hasCustomerName, customerName }}
              />
            )
          }
          hasTitle={!!subheaderText}
          size={TitleSize.MEDIUM}
        />
        {subheaderText && (
          <SubheaderBodyText size={BodyTextSize.SMALL} text={subheaderText} color={colors.textSecondary} />
        )}
        {coverageModalContents && coverageModalContents.length > 0 && (
          <CoverageContainer testID={tID(`${testIDPrefix}-contentContainer`)}>
            {coverageModalContents.map((content: CostMessagingModalContent, index: number) => {
              const { contentType, title } = content
              const icon = getCoverageIcon(contentType)

              return (
                <CoverageContentRow key={`coverageContent-${index}`}>
                  <View>{icon}</View>
                  <CoverageContentText>
                    <CoverageContentHeader>
                      <BodyText
                        size={BodyTextSize.DEFAULT}
                        color={colors.textPrimary}
                        text={title}
                        // Keep the activity badge on a separate line for mobile
                        style={isMobileSized ? { flexBasis: '100%' } : {}}
                      />
                      {renderActivityBadges(contentType)}
                    </CoverageContentHeader>
                    <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary} wrap text={content.description} />
                  </CoverageContentText>
                </CoverageContentRow>
              )
            })}
          </CoverageContainer>
        )}
      </View>

      {(primaryButton || secondaryButton) && (
        <ButtonContainer alignment={buttonAlignment} testID={tID(`${testIDPrefix}-buttonContainer`)}>
          {secondaryButton && (
            <TertiaryButton
              onPress={secondaryButton.onPress}
              text={secondaryButton.text}
              testID={tID(secondaryButton.testID ?? 'CoverageBenefitsSummary-secondaryButton')}
              fullWidth={isMobileSized}
              customTextColor={colors.textActive}
            />
          )}
          {primaryButton && (
            <PrimaryButton
              onPress={primaryButton.onPress}
              text={primaryButton.text}
              testID={tID(primaryButton.testID ?? 'CoverageBenefitsSummary-primaryButton')}
              fullWidth={isMobileSized}
            />
          )}
        </ButtonContainer>
      )}
    </>
  )
}
